import type { AssistantUiContext } from './assistantApi'

/** Human labels for common backoffice routes the assistant should ground on. */
const EXACT_VIEWS: Record<string, string> = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/entry': 'Entry / company switcher',
  '/onboarding': 'Onboarding',
  '/categories': 'Categories',
  '/company': 'Company settings',
  '/company/holidays': 'Public holidays',
  '/calendar': 'Calendar',
  '/events': 'Events',
  '/inventory': 'Inventory',
  '/inventory/new': 'New inventory item',
  '/addons': 'Add-ons',
  '/offerings': 'Offerings',
  '/plans': 'Plans',
  '/plans/configs': 'Plan configs',
  '/orders': 'Orders',
  '/orders/manage': 'Manage orders',
  '/stores': 'Stores',
  '/stock': 'Stock',
  '/shifts': 'Shifts',
  '/shifts/generate': 'Generate shifts',
  '/reporting': 'Reporting',
  '/team': 'Team',
  '/workers': 'Workers',
  '/clients': 'Clients',
  '/settings': 'Settings',
  '/platform': 'Platform',
  '/platform/catalog': 'Platform catalog',
  '/platform/companies': 'Platform companies',
  '/platform/rights': 'Platform rights',
  '/platform/ai': 'Platform AI settings',
  // The rest of Platform, named as in the side menu (navConfig.ts).
  '/platform/packages': 'Packages',
  '/platform/users': 'Users',
  '/platform/couriers': 'Couriers',
  '/platform/dispatch': 'Dispatch',
  '/platform/support': 'Support',
  '/platform/devices': 'Devices',
  '/platform/payments': 'Payments',
  '/platform/wallet': 'Wallet',
  '/platform/rewards/promo-codes': 'Promo codes',
  '/platform/rewards/loyalty': 'Loyalty',
  '/platform/rewards/campaigns': 'TOU campaigns',
  '/platform/invoices': 'Invoices',
  '/platform/system': 'System',
}

const PREFIX_VIEWS: Array<{ prefix: string; label: string }> = [
  { prefix: '/inventory/', label: 'Inventory item setup' },
  { prefix: '/events/', label: 'Event attendance' },
  { prefix: '/clients/', label: 'Client detail' },
  { prefix: '/orders/', label: 'Orders' },
  { prefix: '/platform/support/', label: 'Support request' },
  { prefix: '/platform/rewards/campaigns/', label: 'TOU campaign' },
  { prefix: '/platform/', label: 'Platform admin' },
  { prefix: '/onboarding/', label: 'Onboarding' },
]

export function viewLabelForPath(pathname: string): string {
  const path = pathname.split('?')[0] || '/'
  if (EXACT_VIEWS[path]) return EXACT_VIEWS[path]
  for (const { prefix, label } of PREFIX_VIEWS) {
    if (path.startsWith(prefix)) return label
  }
  return path
}

/** Pull useful ids from the current path for assistant grounding. */
export function selectionFromPath(pathname: string): Record<string, string> {
  const path = pathname.split('?')[0] || '/'
  const selection: Record<string, string> = {}
  const inv = path.match(/^\/inventory\/([^/]+)(?:\/setup)?$/)
  if (inv?.[1] && inv[1] !== 'new') selection.inventoryId = inv[1]
  const event = path.match(/^\/events\/([^/]+)\/attendance$/)
  if (event?.[1]) selection.occurrenceId = event[1]
  const client = path.match(/^\/clients\/([^/]+)$/)
  if (client?.[1]) selection.clientId = client[1]
  return selection
}

export function buildAssistantUiContext(input: {
  pathname: string
  search?: string
  companyId?: string | null
  companyName?: string | null
  companyRole?: string | null
  activePersona?: string | null
  activeRole?: string | null
  isPlatformAdmin?: boolean
  isOwner?: boolean
  permissions?: string[]
  impersonation?: { targetUserId: string; companyId?: string | null; targetName?: string | null } | null
}): AssistantUiContext {
  const path = `${input.pathname || '/'}${input.search || ''}`
  const pathnameOnly = (input.pathname || '/').split('?')[0] || '/'
  return {
    path,
    viewLabel: viewLabelForPath(pathnameOnly),
    companyId: input.companyId ?? null,
    companyName: input.companyName ?? null,
    companyRole: input.companyRole ?? null,
    activePersona: input.activePersona ?? null,
    activeRole: input.activeRole ?? null,
    isPlatformAdmin: Boolean(input.isPlatformAdmin),
    isOwner: Boolean(input.isOwner),
    permissions: input.permissions?.length ? [...input.permissions].sort() : [],
    impersonation: input.impersonation
      ? {
          targetUserId: input.impersonation.targetUserId,
          companyId: input.impersonation.companyId ?? null,
          targetName: input.impersonation.targetName ?? null,
        }
      : null,
    selection: selectionFromPath(pathnameOnly),
    client: 'backoffice',
    uiActions: {
      confirmAll: true,
      rejectAll: true,
    },
  }
}

/** Short selection chips for the chat header (omit raw UUIDs). */
function selectionSummaryLabels(selection?: Record<string, string> | null): string[] {
  if (!selection) return []
  const labels: string[] = []
  if (selection.inventoryId) labels.push('inventory')
  if (selection.clientId) labels.push('client')
  if (selection.occurrenceId) labels.push('occurrence')
  return labels
}

function roleSummaryLabel(ctx: AssistantUiContext): string | null {
  if (ctx.isPlatformAdmin) return 'Platform admin'
  if (ctx.isOwner) return 'Owner'
  const persona = ctx.activePersona?.trim()
  if (persona) return persona
  const role = ctx.activeRole?.trim()
  if (role) return role
  const companyRole = ctx.companyRole?.trim()
  if (companyRole) return companyRole
  return null
}

/**
 * The chat's context line, from the same uiContext sent on chat POST (which
 * this does not change).
 *
 * `primary` is the pill: company · page (· what is selected), e.g.
 * "Unusual Projects OÜ · Packages". It never shows a raw path or the role.
 * `details` is its tooltip: path · role · impersonation.
 */
export function formatAssistantContextSummary(ctx: AssistantUiContext): {
  primary: string
  details: string
} {
  const company = ctx.companyName?.trim() || 'No company'
  // A page without a label falls back to its path; the pill leaves it out.
  const view = ctx.viewLabel && !ctx.viewLabel.startsWith('/') ? ctx.viewLabel : null
  const primary = [company, view, ...selectionSummaryLabels(ctx.selection)]
    .filter(Boolean)
    .join(' · ')

  const detailParts: string[] = []
  if (ctx.path) detailParts.push(ctx.path)
  const role = roleSummaryLabel(ctx)
  if (role) detailParts.push(role)
  if (ctx.impersonation) {
    const target =
      ctx.impersonation.targetName?.trim() || ctx.impersonation.targetUserId || 'user'
    detailParts.push(`Impersonating ${target}`)
  }

  return { primary, details: detailParts.join(' · ') }
}
